import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useRef, useState } from "react";

import { uploadFileOrReturnPublicUrl } from "@/cards/common/utils/createCardTransformShared";
import { HOPE_SANS } from "@/shared/branding/thehopeonebrand";

import { MAX_COLUMN_IMAGES } from "../utils/columnExperience";

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#5E8F72",
  mist: "#EEF5EF",
  paper: "#FFFFFF",
  muted: "#68766F",
  border: "rgba(16,44,36,.12)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: c.paper,
    fontFamily: HOPE_SANS,
    "& fieldset": { borderColor: c.border },
    "&:hover fieldset": { borderColor: c.green },
    "&.Mui-focused fieldset": { borderColor: c.forest },
  },
  "& .MuiInputLabel-root": { fontFamily: HOPE_SANS, color: c.muted },
} as const;

type ImpactColumnMediaUploadProps = {
  kind: "image" | "video";
  url?: string;
  urls?: string[];
  onUrl?: (url: string) => void;
  onUrls?: (urls: string[]) => void;
  maxFiles?: number;
  label?: string;
  hideLinkField?: boolean;
};

export default function ImpactColumnMediaUpload({
  kind,
  url,
  urls,
  onUrl,
  onUrls,
  maxFiles = kind === "image" ? MAX_COLUMN_IMAGES : 1,
  label,
  hideLinkField = false,
}: ImpactColumnMediaUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const accept = kind === "image" ? "image/*" : "video/*";
  const noun = kind === "image" ? "image" : "video";
  const multiple = kind === "image" && maxFiles > 1;
  const current = multiple
    ? (urls && urls.length ? urls : url ? [url] : []).filter(Boolean)
    : url
      ? [url]
      : [];
  const remaining = Math.max(0, maxFiles - current.length);

  const setFiles = (next: string[]) => {
    const unique = Array.from(new Set(next.filter(Boolean))).slice(0, maxFiles);
    if (multiple) {
      onUrls?.(unique);
      if (!onUrls) onUrl?.(unique[0] ?? "");
    } else {
      onUrl?.(unique[0] ?? "");
    }
  };

  const uploadFiles = async (files: File[]) => {
    const valid = files.filter((file) =>
      kind === "image" ? file.type.startsWith("image/") : file.type.startsWith("video/")
    );
    if (!valid.length) {
      enqueueSnackbar(`Please choose ${kind === "image" ? "image" : "video"} files.`, {
        variant: "warning",
      });
      return;
    }
    const picked = valid.slice(0, remaining);
    if (!picked.length) {
      enqueueSnackbar(`You can add up to ${maxFiles} ${noun}${maxFiles === 1 ? "" : "s"}.`, {
        variant: "warning",
      });
      return;
    }

    setBusy(true);
    try {
      const uploaded: string[] = [];
      for (const file of picked) {
        const nextUrl = await uploadFileOrReturnPublicUrl(file, "Impact_Card_page");
        if (nextUrl) uploaded.push(nextUrl);
      }
      if (!uploaded.length) throw new Error("Upload returned no URL");
      setFiles([...current, ...uploaded]);
    } catch {
      enqueueSnackbar(`Could not upload this ${noun}. Try a smaller file.`, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const addPastedUrl = () => {
    const next = pasteUrl.trim();
    if (!next) return;
    if (!remaining) {
      enqueueSnackbar(`You can add up to ${maxFiles} ${noun}${maxFiles === 1 ? "" : "s"}.`, {
        variant: "warning",
      });
      return;
    }
    setFiles([...current, next]);
    setPasteUrl("");
  };

  return (
    <Stack spacing={1.25} sx={{ mb: 1.5 }}>
      {current.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: multiple ? "repeat(3, minmax(0, 1fr))" : "1fr",
            gap: 1,
          }}
        >
          {current.map((item) => (
            <Box
              key={item}
              sx={{
                position: "relative",
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: "#102C24",
                aspectRatio: multiple ? "1" : undefined,
              }}
            >
              {kind === "image" ? (
                <Box
                  component="img"
                  src={item}
                  alt=""
                  sx={{
                    display: "block",
                    width: "100%",
                    height: multiple ? "100%" : 180,
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Box
                  component="video"
                  src={item}
                  controls
                  sx={{
                    display: "block",
                    width: "100%",
                    maxHeight: 200,
                    bgcolor: "#000",
                  }}
                />
              )}
              <IconButton
                size="small"
                aria-label={`Remove ${noun}`}
                onClick={() => setFiles(current.filter((entry) => entry !== item))}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 26,
                  height: 26,
                  bgcolor: "rgba(16,44,36,.72)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(16,44,36,.9)" },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : null}

      {remaining > 0 ? (
        <Box
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            const files = Array.from(event.dataTransfer.files ?? []);
            if (files.length) void uploadFiles(files);
          }}
          onClick={() => {
            if (!busy) inputRef.current?.click();
          }}
          sx={{
            border: `1.5px dashed ${dragOver ? c.forest : c.border}`,
            borderRadius: "16px",
            bgcolor: dragOver ? c.mist : "rgba(255,255,255,.72)",
            p: 2,
            cursor: busy ? "wait" : "pointer",
            textAlign: "center",
          }}
        >
          {busy ? (
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={22} sx={{ color: c.forest }} />
              <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.8rem", color: c.muted }}>
                Uploading {noun}…
              </Typography>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={0.75}>
              <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: c.forest }} />
              <Typography sx={{ fontFamily: HOPE_SANS, fontWeight: 800, fontSize: "0.86rem", color: c.forestDeep }}>
                {label ?? (multiple ? `Upload images (${current.length}/${maxFiles})` : `Upload ${noun}`)}
              </Typography>
              <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.72rem", color: c.muted }}>
                {multiple
                  ? `Drop up to ${remaining} more, or click to browse`
                  : "Drop a file here or click to browse"}
              </Typography>
            </Stack>
          )}
          <input
            ref={inputRef}
            hidden
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = "";
              if (files.length) void uploadFiles(files);
            }}
          />
        </Box>
      ) : (
        <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.72rem", color: c.muted }}>
          {maxFiles} {noun}s added. Remove one to upload another.
        </Typography>
      )}

      {remaining > 0 && !hideLinkField ? (
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            label={multiple ? "Or paste image URL" : `Or paste ${noun} URL`}
            value={multiple ? pasteUrl : url ?? pasteUrl}
            onChange={(event) => {
              if (multiple) setPasteUrl(event.target.value);
              else onUrl?.(event.target.value);
            }}
            onKeyDown={(event) => {
              if (multiple && event.key === "Enter") {
                event.preventDefault();
                addPastedUrl();
              }
            }}
            sx={fieldSx}
          />
          {multiple ? (
            <Button
              onClick={addPastedUrl}
              sx={{ textTransform: "none", color: c.forest, fontWeight: 700, flexShrink: 0 }}
            >
              Add
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}
